import { App } from "@capacitor/app";
import {
	CapacitorBarcodeScanner,
	CapacitorBarcodeScannerAndroidScanningLibrary,
	CapacitorBarcodeScannerTypeHint,
} from "@capacitor/barcode-scanner";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { router } from ".";
import { postDeviceToken } from "./api/users_{userId}_device-tokens";
import { state } from "./state";

const onAndroid = Capacitor.getPlatform() === "android";
// const oniOS = Capacitor.getPlatform() === "ios";
const onMobile = Capacitor.getPlatform() !== "web";

/**
 * @typedef {'appFinishedLoading' | 'registeredNotificationsToken'} MobileFlag
 */

const createMobileFlags = () => {
	/** @type {Record<MobileFlag, boolean>} */
	const flags = {
		appFinishedLoading: false,
		registeredNotificationsToken: false,
	};

	/**
	 * @param {MobileFlag} flag
	 */
	const getValue = (flag) => flags[flag];

	/**
	 * @param {MobileFlag} flag
	 * @param {boolean} value
	 */
	const setValue = (flag, value) => {
		flags[flag] = value;
	};

	return {
		getValue,
		setValue,
	};
};

const MobileFlags = createMobileFlags();

/**
 * Loads the barcode-scanner and displays an overlay for handling scanner feedback.
 * @param {string} instructions
 * @returns {Promise<string | null>}
 */
async function scanBarcode(instructions) {
	try {
		const result = await CapacitorBarcodeScanner.scanBarcode({
			hint: CapacitorBarcodeScannerTypeHint.ALL,
			scanInstructions: instructions ?? "Scan",
			android: {
				scanningLibrary:
					CapacitorBarcodeScannerAndroidScanningLibrary.MLKIT,
			},
		});
		return result?.ScanResult ?? null;
	} catch (error) {
		console.log(error);
		return null;
	}
}

/**
 * @typedef {'farmcamsim' | 'fencealarm' | 'fencealarm2' | 'smartplug'} ListenerType
 */

/**
 * @typedef {Record<string, function>} CallbackMap
 */

/**
 * @typedef {Record<ListenerType, CallbackMap>} NotificationListener
 */

/**
 * @type {import('@capacitor/push-notifications').PushNotificationSchema | undefined}
 */
let initialNotification;

function getInitialNotification() {
	const value = initialNotification;
	initialNotification = undefined;
	return value;
}

/**
 * @type {{
 *  farmcamsim: Record<string,() => void>
 *  fencealarm: Record<string,() => void>
 *  fencealarm2: Record<string,() => void>
 *  smartplug: Record<string,() => void>
 *  smartplug2: Record<string,() => void>
 * }}
 */
const notificationListeners = {
	farmcamsim: {},
	fencealarm: {},
	fencealarm2: {},
	smartplug: {},
	smartplug2: {},
};

/**
 * @param {"farmcamsim"|"fencealarm"|"fencealarm2"|"smartplug"|"smartplug2"} deviceType
 * @param {string} deviceId
 * @param {() => void} callback // Note: Consider forwarding the notification data to the callback
 */
const addNotificationListener = (deviceType, deviceId, callback) => {
	if (!onMobile) return;

	notificationListeners[deviceType][deviceId] = callback;
};

/**
 * @param {"farmcamsim"|"fencealarm"|"fencealarm2"|"smartplug"|"smartplug2"} deviceType
 * @param {string} deviceId
 */
const removeNotificationListener = (deviceType, deviceId) => {
	if (!onMobile) return;
	delete notificationListeners[deviceType][deviceId];
};

async function registerNotifications() {
	let permStatus = await PushNotifications.checkPermissions();

	if (permStatus.receive === "prompt") {
		permStatus = await PushNotifications.requestPermissions();
	}

	if (permStatus.receive !== "granted") {
		// Handle error
		throw new Error("User denied permissions!");
	}

	await PushNotifications.register();
}

async function startNotificationListeners() {
	await PushNotifications.addListener("registration", async (token) => {
		try {
			if (!token?.value || !(await state.getSignedInUser()).Id) return;

			await postDeviceToken(token.value);
			MobileFlags.setValue("registeredNotificationsToken", true);
		} catch (error) {
			console.error("Failed to register token", error);
		}
	});

	await PushNotifications.addListener("registrationError", (err) => {
		console.error("Registration error: ", err.error);
	});

	await PushNotifications.addListener(
		"pushNotificationReceived",
		async (notification) => {
			if (!(await state.getSignedInUser())?.Id) return;

			try {
				if (!notification.data?.Imei) {
					if (notification.data?.Device === "fencealarm") {
						notificationListeners["fencealarm"][
							notification.data.Imei
						]?.();
					} else if (notification.data?.Device === "fencealarm2") {
						notificationListeners["fencealarm2"][
							notification.data.Imei
						]?.();
					} else if (notification.data?.Device === "smartplug") {
						notificationListeners["smartplug"][
							notification.data.Imei
						]?.();
					}
				} else if (notification.data?.Imsi) {
					if (notification.data?.Device === "farmcamsim") {
						notificationListeners["farmcamsim"][
							notification.data.Imsi
						]?.();
					}
				}
			} catch (error) {
				// Handle callback error
			}
		},
	);

	await PushNotifications.addListener(
		"pushNotificationActionPerformed",
		async (action) => {
			if (!MobileFlags.getValue("appFinishedLoading")) {
				initialNotification = action.notification;
				return;
			}

			if (!(await state.getSignedInUser())?.Id) return;

			const imei = action.notification.data?.Imei;
			if (imei) {
				router.jump(`/devices?id=${imei}`);
			}
		},
	);
}

let lastActiveAt = Date.now();
let lastInactiveAt = Date.now();

/** @typedef {Record<string, (n: number) => void>} StateListener */

/** @type {StateListener} */
const focusListeners = {};

/** @type {StateListener} */
const blurListeners = {};

async function startStateListener() {
	App.addListener("appStateChange", (state) => {
		if (state.isActive) {
			lastActiveAt = Date.now();
			const secondsInactive = (Date.now() - lastInactiveAt) / 1000;

			Object.keys(focusListeners).forEach((key) => {
				try {
					focusListeners[key]?.(secondsInactive);
				} catch (error) {
					console.error("Error in focus listener", error);
				}
			});
		} else {
			lastInactiveAt = Date.now();
			const secondsActive = (Date.now() - lastActiveAt) / 1000;

			Object.keys(blurListeners).forEach((key) => {
				try {
					blurListeners[key]?.(secondsActive);
				} catch (error) {
					console.error("Error in blur listener", error);
				}
			});
		}
	});
}

/**
 * @param {string} key
 * @param {(n: number) => void} callback
 */
function addFocusListener(key, callback) {
	focusListeners[key] = callback;
}

/**
 * @param {string} key
 */
function removeFocusListener(key) {
	delete focusListeners[key];
}

/**
 * @param {string} key
 * @param {(n: number) => void} callback
 */
function addBlurListener(key, callback) {
	blurListeners[key] = callback;
}

/**
 * @param {string} key
 */
function removeBlurListener(key) {
	delete focusListeners[key];
}

export {
	addBlurListener,
	addFocusListener,
	addNotificationListener,
	getInitialNotification,
	MobileFlags,
	onAndroid,
	onMobile,
	registerNotifications,
	removeBlurListener,
	removeFocusListener,
	removeNotificationListener,
	scanBarcode,
	startNotificationListeners,
	startStateListener,
};
